import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Header from '../components/header';
import { AuthContext } from '../context/AuthContext';
import useAdministrationAccess from '../hooks/useAdministrationAccess';
import DriveAdministration from '../components/DriveAdministration';
import { ADMIN_ASSISTANT_URL, ADMIN_USERS_URL } from '../constants';

const control = 'w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100';
const button = 'rounded-lg px-4 py-2 font-medium border disabled:opacity-50 disabled:cursor-not-allowed';
const requestOptions = token => ({ headers: { Authorization: `Bearer ${token}` } });
const toDraft = data => ({ instructions: data.instructions || '', model: data.model || '', reasoningEffort: data.reasoningEffort || 'high' });
const formatDate = value => value && !Number.isNaN(new Date(value).getTime())
  ? new Date(value).toLocaleString('es-CL') : 'Sin registro';

function AssistantEditor({ token, onDenied, onPendingChange }) {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(toDraft({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [conflict, setConflict] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const mounted = useRef(true);
  const savingRef = useRef(false);
  const dirty = Boolean(saved) && JSON.stringify(draft) !== JSON.stringify(toDraft(saved));
  useEffect(() => { onPendingChange(dirty || saving); }, [dirty, saving, onPendingChange]);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(''); setNotice('');
    axios.get(ADMIN_ASSISTANT_URL, { ...requestOptions(token), signal: controller.signal })
      .then(response => {
        if (controller.signal.aborted) return;
        setSaved(response.data); setDraft(toDraft(response.data)); setConflict(false);
      }).catch(err => {
        if (controller.signal.aborted) return;
        if ([401, 403].includes(err.response?.status)) onDenied(err.response.status);
        else setError('No se pudo cargar la configuración. Puedes volver a intentar.');
      }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [token, attempt, onDenied]);

  useEffect(() => {
    if (!dirty && !saving) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, saving]);

  const reload = () => {
    if (dirty && !window.confirm('¿Descartar los cambios sin guardar y cargar la versión actual?')) return;
    setAttempt(value => value + 1);
  };
  const save = async event => {
    event.preventDefault();
    if (!saved || !dirty || conflict || savingRef.current) return;
    if (!draft.model.trim() || !draft.instructions.trim()) {
      setError('Completa el modelo y las instrucciones antes de guardar.'); return;
    }
    savingRef.current = true; setSaving(true); setError(''); setNotice('');
    try {
      const response = await axios.put(ADMIN_ASSISTANT_URL, { ...draft, model: draft.model.trim(), revision: saved.revision }, requestOptions(token));
      if (!mounted.current) return;
      setSaved(response.data); setDraft(toDraft(response.data));
      setNotice('Configuración guardada. Se aplicará a las siguientes consultas.');
    } catch (err) {
      if (!mounted.current) return;
      const status = err.response?.status;
      if ([401, 403].includes(status)) onDenied(status);
      else if (err.response?.data?.code === 'ASSISTANT_SETTINGS_CONFLICT') {
        setConflict(true); setError('Otra persona modificó la configuración. Copia tus cambios si quieres conservarlos y carga la versión actual antes de editar nuevamente.');
      } else setError(err.response?.data?.error || 'No se pudo guardar. Tus cambios permanecen en el formulario; comprueba el estado antes de reintentar.');
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  if (loading) return <p role="status">Cargando configuración…</p>;
  return (
    <section aria-labelledby="assistant-heading">
      <h2 id="assistant-heading" className="text-xl font-semibold">Configuración del asistente</h2>
      <p className="text-gray-600 mt-2 mb-6">Define cómo responde Abogadacio. Los cambios se aplican a las nuevas consultas, sin interrumpir las que están en curso.</p>
      {error && <p role="alert" className="rounded-lg bg-red-50 text-red-800 p-4 mb-4">{error}</p>}
      {notice && <p role="status" className="rounded-lg bg-green-50 text-green-800 p-4 mb-4">{notice}</p>}
      {!saved ? <button type="button" onClick={reload} className={button}>Reintentar</button> : (
        <form onSubmit={save}>
          <fieldset disabled={saving} className="space-y-5">
            <div className="grid gap-5 md:grid-cols-2">
              <label className="block font-medium">Modelo
                <input className={`${control} mt-2`} value={draft.model} maxLength={200} required
                  onChange={event => setDraft({ ...draft, model: event.target.value })} placeholder="gpt-6-luna" />
              </label>
              <label className="block font-medium">Nivel de razonamiento
                <select className={`${control} mt-2`} value={draft.reasoningEffort}
                  onChange={event => setDraft({ ...draft, reasoningEffort: event.target.value })}>
                  <option value="none">Sin razonamiento (none)</option><option value="low">Bajo (low)</option>
                  <option value="medium">Medio (medium)</option><option value="high">Alto (high)</option>
                  <option value="xhigh">Muy alto (xhigh)</option><option value="max">Máximo (max)</option>
                </select>
              </label>
            </div>
            <label className="block font-medium">Instrucciones del asistente
              <textarea className={`${control} mt-2 font-mono text-sm leading-relaxed`} rows={22} required maxLength={100000}
                value={draft.instructions} onChange={event => setDraft({ ...draft, instructions: event.target.value })} />
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <button type="submit" disabled={!dirty || conflict} className={`${button} bg-blue-700 text-white`}>{saving ? 'Guardando…' : 'Guardar cambios'}</button>
              <button type="button" onClick={reload} className={button}>Cargar versión actual</button>
              <span className="text-sm text-gray-600">{dirty ? 'Hay cambios sin guardar' : 'Sin cambios pendientes'}</span>
            </div>
          </fieldset>
          <p className="mt-6 text-sm text-gray-500">Última edición: {formatDate(saved.updatedAt)}{saved.updatedBy ? ` · Cuenta: ${saved.updatedBy}` : ''}</p>
        </form>
      )}
    </section>
  );
}

function UserAction({ selected, token, onDenied, onCancel, onFinished, onBusy }) {
  const { account, action } = selected;
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const target = account.email || account.id;
  const deleting = action === 'delete';
  const submit = async event => {
    event.preventDefault();
    if (pending.current) return;
    if (!deleting && (!password.trim() || password.length < 8 || password !== repeat)) {
      setError('Las contraseñas deben coincidir y tener al menos 8 caracteres.'); return;
    }
    if (deleting && confirmation.trim() !== target) {
      setError('La confirmación no coincide con la cuenta seleccionada.'); return;
    }
    pending.current = true; setBusy(true); onBusy(true); setError('');
    try {
      const url = `${ADMIN_USERS_URL}/${encodeURIComponent(account.id)}`;
      if (deleting) await axios.delete(url, { ...requestOptions(token), data: { confirmation: confirmation.trim() } });
      else await axios.put(`${url}/password`, { password }, requestOptions(token));
      if (!mounted.current) return;
      setPassword(''); setRepeat('');
      onFinished({ account, deleted: deleting });
    } catch (err) {
      if (!mounted.current) return;
      if ([401, 403].includes(err.response?.status)) onDenied(err.response.status);
      else setError(err.response?.data?.error || 'No se pudo completar la operación. Actualiza la lista antes de reintentar.');
    } finally {
      pending.current = false;
      if (mounted.current) { setBusy(false); onBusy(false); }
    }
  };
  return (
    <section aria-label={deleting ? 'Confirmar eliminación de usuario' : 'Cambiar contraseña de usuario'} className="p-5 mb-6 border rounded-lg bg-gray-50">
      <h3 className="text-lg font-semibold">{deleting ? 'Eliminar usuario' : 'Cambiar contraseña'}: {target}</h3>
      <p className="text-sm text-gray-600 my-3">{deleting
        ? 'Se eliminará la cuenta de acceso. El historial de conversaciones se conservará. Esta acción no cierra sesiones ya abiertas.'
        : 'La nueva contraseña se usará en el próximo inicio de sesión. Las sesiones ya abiertas no se cierran con este cambio.'}</p>
      {error && <p role="alert" className="text-red-800 mb-4">{error}</p>}
      <form onSubmit={submit}>
        <fieldset disabled={busy} className="space-y-4 max-w-xl">
          {deleting ? <label className="block">Escribe {target} para confirmar
            <input className={`${control} mt-2`} autoComplete="off" value={confirmation} onChange={event => setConfirmation(event.target.value)} required />
          </label> : <>
            <label className="block">Nueva contraseña
              <input type="password" className={`${control} mt-2`} autoComplete="new-password" value={password} minLength={8} maxLength={72} required onChange={event => setPassword(event.target.value)} />
            </label>
            <label className="block">Repetir nueva contraseña
              <input type="password" className={`${control} mt-2`} autoComplete="new-password" value={repeat} minLength={8} maxLength={72} required onChange={event => setRepeat(event.target.value)} />
            </label>
            <p className="text-sm text-gray-500">Mínimo 8 caracteres.</p>
          </>}
          <div className="flex gap-3">
            <button type="submit" className={`${button} ${deleting ? 'bg-red-700' : 'bg-blue-700'} text-white`} disabled={deleting && confirmation.trim() !== target}>
              {busy ? 'Procesando…' : deleting ? 'Confirmar eliminación' : 'Actualizar contraseña'}
            </button>
            <button type="button" className={button} onClick={onCancel}>Cancelar</button>
          </div>
        </fieldset>
      </form>
    </section>
  );
}

function UsersList({ token, onDenied, currentUserId }) {
  const [page, setPage] = useState({ items: [], nextCursor: null });
  const [cursors, setCursors] = useState([null]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [selected, setSelected] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const cursor = cursors[index];
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError('');
    axios.get(ADMIN_USERS_URL, { ...requestOptions(token), params: { limit: 25, ...(cursor ? { cursor } : {}) }, signal: controller.signal })
      .then(response => { if (!controller.signal.aborted) { setPage(response.data); setFilter(''); } })
      .catch(err => {
        if (controller.signal.aborted) return;
        if ([401, 403].includes(err.response?.status)) onDenied(err.response.status);
        else setError(err.response?.data?.error || 'No se pudo cargar la lista de usuarios.');
      }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [token, cursor, attempt, onDenied]);
  const visible = page.items.filter(item => [item.username, item.email, item.id]
    .some(value => String(value || '').toLocaleLowerCase().includes(filter.trim().toLocaleLowerCase())));
  return (
    <section aria-labelledby="users-heading">
      <h2 id="users-heading" className="text-xl font-semibold">Usuarios registrados</h2>
      <p className="text-gray-600 mt-2 mb-6">Revisa las cuentas, cambia contraseñas o elimina usuarios. Las cuentas con permiso administrativo están protegidas contra eliminación.</p>
      {notice && <p role="status" className="bg-green-50 text-green-800 p-4 rounded-lg mb-4">{notice}</p>}
      {selected && <UserAction key={`${selected.account.id}/${selected.action}`} selected={selected} token={token} onDenied={onDenied}
        onCancel={() => setSelected(null)} onBusy={setActionBusy} onFinished={({ account, deleted }) => {
          if (deleted) setPage(value => ({ ...value, items: value.items.filter(item => item.id !== account.id) }));
          setNotice(deleted ? `Usuario ${account.email || account.id} eliminado.` : `Contraseña de ${account.email || account.id} actualizada.`);
          setSelected(null); setActionBusy(false);
        }} />}
      <label className="block max-w-xl mb-5">Filtrar esta página por nombre, correo o ID
        <input type="search" className={`${control} mt-2`} value={filter} onChange={event => setFilter(event.target.value)} disabled={loading || actionBusy} />
      </label>
      {loading && <p role="status">Cargando usuarios…</p>}
      {error && <div role="alert" className="bg-red-50 text-red-800 p-4 rounded-lg mb-4">{error} <button type="button" className="underline" onClick={() => setAttempt(value => value + 1)}>Reintentar</button></div>}
      {!loading && !error && <>
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm text-left">
            <caption className="sr-only">Cuentas registradas, página {index + 1}</caption>
            <thead className="bg-gray-50"><tr>{['Nombre', 'Correo', 'Identificador', 'Registro', 'Administración', 'Acciones'].map(label => <th key={label} className="p-3 font-semibold">{label}</th>)}</tr></thead>
            <tbody>{visible.map(item => <tr key={item.id} className="border-t">
              <td className="p-3">{item.username || 'Sin nombre'}</td><td className="p-3">{item.email || 'Sin correo'}</td>
              <td className="p-3 font-mono text-xs">{item.id}</td><td className="p-3 whitespace-nowrap">{formatDate(item.createdAt)}</td>
              <td className="p-3">{item.isAdmin ? 'Administrador' : 'Usuario'}</td>
              <td className="p-3"><div className="flex gap-2 whitespace-nowrap">
                <button type="button" className={`${button} text-blue-700`} disabled={actionBusy} aria-label={`Cambiar contraseña de ${item.email || item.id}`}
                  onClick={() => { setSelected({ account: item, action: 'password' }); setNotice(''); }}>Cambiar contraseña</button>
                <button type="button" className={`${button} text-red-700`} disabled={actionBusy || item.isAdmin || item.id === currentUserId}
                  title={item.id === currentUserId ? 'No puedes eliminar tu propia cuenta' : item.isAdmin ? 'Retira primero su permiso administrativo' : 'Eliminar cuenta'}
                  aria-label={`Eliminar usuario ${item.email || item.id}`} onClick={() => { setSelected({ account: item, action: 'delete' }); setNotice(''); }}>Eliminar</button>
              </div></td>
            </tr>)}</tbody>
          </table>
        </div>
        {!visible.length && <p className="py-5 text-gray-600">{filter ? 'No hay coincidencias en esta página.' : 'No hay usuarios en esta página.'}</p>}
        <p className="text-sm text-gray-500 mt-3">Página {index + 1} · {page.items.length} cuentas recibidas · {visible.length} visibles</p>
      </>}
      <div className="flex flex-wrap gap-3 mt-5">
        <button type="button" className={button} disabled={loading || actionBusy || index === 0} onClick={() => { setSelected(null); setIndex(value => value - 1); }}>Anterior</button>
        <button type="button" className={button} disabled={loading || actionBusy || Boolean(error) || !page.nextCursor} onClick={() => {
          setSelected(null);
          setCursors(values => [...values.slice(0, index + 1), page.nextCursor]); setIndex(value => value + 1);
        }}>Siguiente</button>
        <button type="button" className={button} disabled={loading || actionBusy} onClick={() => { setSelected(null); setCursors([null]); setIndex(0); setAttempt(value => value + 1); }}>Actualizar lista</button>
      </div>
    </section>
  );
}

function AdministrationContent({ token, logout }) {
  const { session, loading, error, reload } = useAdministrationAccess();
  const [denied, setDenied] = useState(null);
  const [section, setSection] = useState('assistant');
  const [pendingChanges, setPendingChanges] = useState(false);
  // Callback estable: evita recargar y descartar el formulario al renderizar el contenedor.
  const onDenied = React.useCallback(status => setDenied(status), []);
  const reloadAccess = () => { setDenied(null); reload(); };
  const canLeave = () => !pendingChanges || window.confirm('¿Salir sin guardar los cambios del asistente?');
  return (
    <div className="min-h-screen bg-gray-50">
      <Header onBeforeLogout={canLeave} />
      <main className="max-w-6xl mx-auto px-4 py-8 sm:px-8">
        <Link to="/dashboard" className="text-blue-700 underline" onClick={event => { if (!canLeave()) event.preventDefault(); }}>Volver al asistente</Link>
        <h1 className="text-3xl font-semibold mt-5 mb-6">Administración</h1>
        {loading && <p role="status">Comprobando permisos…</p>}
        {(error || denied === 401) && <div role="alert" className="p-5 bg-white border rounded-lg">
          <p>{denied === 401 ? 'Tu sesión venció. Inicia sesión nuevamente.' : error.message}</p>
          {(error?.status === 401 || denied === 401) ? <button className={`${button} mt-3`} onClick={logout}>Volver a iniciar sesión</button>
            : <button className={`${button} mt-3`} onClick={reloadAccess}>Reintentar</button>}
        </div>}
        {!loading && !error && session && (!session.canAdminister || denied === 403) && <div className="p-6 bg-white border rounded-lg" role="alert">
          <h2 className="text-xl font-semibold">Tu cuenta no tiene permisos de administración</h2>
          <p className="mt-3 text-gray-600">Solicita acceso al responsable de la aplicación indicando el correo con el que inicias sesión.</p>
          <button type="button" className={`${button} mt-4`} onClick={reloadAccess}>Volver a comprobar acceso</button>
        </div>}
        {!loading && !error && session?.canAdminister && !denied && <>
          <nav aria-label="Secciones de administración" className="flex flex-wrap gap-3 mb-6">
            <button className={`${button} ${section === 'assistant' ? 'bg-blue-700 text-white' : 'bg-white'}`} aria-pressed={section === 'assistant'} onClick={() => setSection('assistant')}>Asistente</button>
            <button className={`${button} ${section === 'users' ? 'bg-blue-700 text-white' : 'bg-white'}`} aria-pressed={section === 'users'} onClick={() => setSection('users')}>Usuarios</button>
            <button className={`${button} ${section === 'drive' ? 'bg-blue-700 text-white' : 'bg-white'}`} aria-pressed={section === 'drive'} onClick={() => setSection('drive')}>Documentos de Drive</button>
          </nav>
          <div className={`p-5 sm:p-8 bg-white border rounded-xl shadow-sm ${section !== 'assistant' ? 'hidden' : ''}`}>
            <AssistantEditor token={token} onDenied={onDenied} onPendingChange={setPendingChanges} />
          </div>
          {section === 'users' && <div className="p-5 sm:p-8 bg-white border rounded-xl shadow-sm"><UsersList token={token} onDenied={onDenied} currentUserId={session.userId} /></div>}
          {section === 'drive' && <div className="p-5 sm:p-8 bg-white border rounded-xl shadow-sm"><DriveAdministration token={token} onDenied={onDenied} /></div>}
        </>}
      </main>
    </div>
  );
}

export default function Administration() {
  const { token, logout } = useContext(AuthContext);
  return <AdministrationContent key={token} token={token} logout={logout} />;
}
